import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { FieldInput, FieldSelect } from "../components/ui/Field";

interface Setting {
  key: string;
  value: string;
}

/** System Configuration (FR-018) — every value here is read from and
 * written to D1 (config_settings), never hardcoded (NFR-002). */
export function Config() {
  const [values, setValues] = useState<Record<string, string>>({});
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["config-settings"], queryFn: () => api.get<{ rows: Setting[] }>("/config/settings").then((r) => r.rows) });

  useEffect(() => {
    if (data) setValues(Object.fromEntries(data.map((s) => [s.key, s.value])));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (key: string) => api.post("/config/settings", { key, value: values[key] }),
    onSuccess: () => {
      toast("Configuration saved");
      qc.invalidateQueries({ queryKey: ["config-settings"] });
    },
  });

  async function saveAll() {
    for (const key of Object.keys(values)) await saveMutation.mutateAsync(key);
  }

  return (
    <Card className="max-w-xl p-6">
      <h2 className="font-semibold text-lg mb-4">System Configuration</h2>
      <div className="grid grid-cols-2 gap-4">
        <FieldInput label="Buyer Notification Email" value={values["buyer_notification_email"] ?? ""} onChange={(e) => setValues((v) => ({ ...v, buyer_notification_email: e.target.value }))} />
        <FieldSelect label="Default Date Format" value={values["default_date_format"] ?? "DD/MMM/YYYY"} onChange={(e) => setValues((v) => ({ ...v, default_date_format: e.target.value }))} options={["DD/MMM/YYYY", "MM/DD/YYYY", "DD/MM/YYYY"].map((f) => ({ value: f, label: f }))} />
        <FieldSelect label="Data Mapping Set" value={values["data_mapping_set"] ?? "Standard"} onChange={(e) => setValues((v) => ({ ...v, data_mapping_set: e.target.value }))} options={["Standard", "GSMA"].map((f) => ({ value: f, label: f }))} />
      </div>
      <div className="flex justify-end mt-6">
        <Button icon={Save} onClick={saveAll}>
          Save Configuration
        </Button>
      </div>
    </Card>
  );
}
